import { ReactElement, useState } from 'react';
import {
  ImagesAuthenticationResetPasswordForgotPasswordBackgroundWebp,
  ImagesAuthenticationResetPasswordResetPasswordSuccessSvg,
} from '@/assets/images_authentication_reset-password.assets';

import Image from '@/components/common/SpiritoryImage';
import {
  PrimaryButton,
  PrimaryButtonLink,
} from '@/components/common/PrimaryButton';
import DefaultLink from '@/components/common/default/DefaultLink';
import { toast } from 'react-toastify';
import doSendPasswordReset from '@/api/doSendPasswordReset';
import { AxiosError } from 'axios';
import { LoadingSpinner } from '@/components/common/LoadingSpinners';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { AuthenticationEnum } from '@/types/translations/AuthenticationEnum';
import { GetServerSideProps } from 'next';
import getSignInServerSideProps from '@/lib/serverSideProps/serverSidePropsPages/getSignInServerSideProps';
import { useRouter } from 'next/router';
import AuthInput from '@/components/auth/AuthInput';
import AuthCommonLayout from '@/components/auth/AuthCommonLayout';
import ForgotPasswordMeta from '@/components/seo/meta/ForgotPasswordMeta';

export default function ForgotPassword() {
  const { query, locale } = useRouter();
  const { returnTo = 'sign_in' } = query;
  const { translate } =
    useTypedTranslation<AuthenticationEnum>('authentication');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordLinkSent, setPasswordLinkSent] = useState(false);
  const [doingSubmit, setDoingSubmit] = useState(false);

  async function handleSubmitResetPassword(
    e: React.FormEvent<HTMLFormElement>
  ) {
    e.preventDefault();
    try {
      setDoingSubmit(true);
      setLoading(true);
      await doSendPasswordReset(email, locale);
      setPasswordLinkSent(true);
    } catch (err) {
      if (err instanceof AxiosError) {
        if (err.response?.data?.meta?.message === 'User not found') {
          toast.error(
            translate(AuthenticationEnum.a_user_with_this_email_was_not_found)
          );
        } else {
          toast.error(
            translate(AuthenticationEnum.an_unexpected_error_occurred)
          );
        }
      }
    } finally {
      setLoading(false);
      setDoingSubmit(false);
    }

  }

  if (passwordLinkSent) {
    return (
      <>
        <ForgotPasswordMeta />

        <AuthCommonLayout
          bgImage={
            ImagesAuthenticationResetPasswordForgotPasswordBackgroundWebp
          }
        >
          <div className="tw-text-center tw-max-w-xl  tw-space-y-8">
            <Image
              src={ImagesAuthenticationResetPasswordResetPasswordSuccessSvg}
              alt="Reset password success"
            />
            <div>
              <h1 className="tw-font-prata tw-font-normal tw-text-5xl tw-leading-[65.04px] tw-text-[#232323] max-nine:tw-text-center max-nine:tw-text-3xl">
                {translate(AuthenticationEnum.password_reset_link_sent)}
              </h1>
              <p className="tw-font-medium tw-text-base tw-leading-[20.16px] tw-font-plusJakartaSans tw-text-[#54606FCC] max-nine:tw-text-center">
                {translate(AuthenticationEnum.password_reset_link_sent_sub)}
              </p>
              <PrimaryButtonLink href="/auth/sign-in" className="tw-w-full">
                {translate(AuthenticationEnum.back_to_sign_in)}
              </PrimaryButtonLink>
            </div>
          </div>
        </AuthCommonLayout>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <ForgotPasswordMeta />

        <AuthCommonLayout
          bgImage={
            ImagesAuthenticationResetPasswordForgotPasswordBackgroundWebp
          }
        >
          <LoadingSpinner
            show={true}
            loadingTitle={translate(AuthenticationEnum.dont_move)}
            loadingSubtitle={translate(AuthenticationEnum.dont_move_sub)}
          />
        </AuthCommonLayout>
      </>
    );
  }

  return (
    <>
      <ForgotPasswordMeta />
      <AuthCommonLayout
        bgImage={ImagesAuthenticationResetPasswordForgotPasswordBackgroundWebp}
      >
        <h1 className="tw-font-prata tw-font-normal tw-text-5xl tw-leading-[65.04px] tw-text-[#232323] max-nine:tw-text-center max-md:tw-text-3xl">
          {translate(AuthenticationEnum.forgot_password)}
        </h1>
        <p className="tw-font-medium tw-text-base tw-leading-[20.16px] tw-font-plusJakartaSans tw-text-[#54606FCC] max-nine:tw-text-center">
          {translate(AuthenticationEnum.forgot_password_sub)}
        </p>
        <form className="tw-contents" onSubmit={handleSubmitResetPassword}>
          <div className="tw-mt-3 tw-relative tw-flex tw-flex-col tw-items-start tw-w-full">
            <AuthInput
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              label={translate(AuthenticationEnum.email)}
              placeholder={translate(AuthenticationEnum.insert_your_email)}
              invalidMessage=""
            />
          </div>
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
            <div>
              <PrimaryButton
                className="tw-w-full !tw-max-w-full tw-py-3 tw-font-bold tw-text-lg"
                type="submit"
                disabled={doingSubmit}
              >
                {translate(AuthenticationEnum.submit)}
              </PrimaryButton>
            </div>
            <DefaultLink
              href={returnTo === 'sign_in' ? '/auth/sign-in' : '/auth/sign-up'}
              className="tw-text-main tw-font-bold"
            >
              {translate(
                returnTo === 'sign_in'
                  ? AuthenticationEnum.back_to_sign_in
                  : AuthenticationEnum.back_to_sign_up
              )}
            </DefaultLink>
          </div>
        </form>
      </AuthCommonLayout>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = getSignInServerSideProps;

ForgotPassword.getLayout = function getLayout(page: ReactElement) {
  return <>{page}</>;
};
